import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import './HeaderUserActions.css';

function notifKey(item) {
  return `${item.entity_type}:${item.entity_id}`;
}

function loadNotifPrefs(userId) {
  if (!userId) return { dismissed: new Set(), read: new Set() };
  try {
    const raw = localStorage.getItem(`colan_hdr_notif_${userId}`);
    if (!raw) return { dismissed: new Set(), read: new Set() };
    const j = JSON.parse(raw);
    return {
      dismissed: new Set(j.dismissed || []),
      read: new Set(j.read || []),
    };
  } catch {
    return { dismissed: new Set(), read: new Set() };
  }
}

function saveNotifPrefs(userId, dismissed, read) {
  if (!userId) return;
  try {
    localStorage.setItem(
      `colan_hdr_notif_${userId}`,
      JSON.stringify({ dismissed: [...dismissed], read: [...read] })
    );
  } catch {
    /* ignore quota */
  }
}

function approvalEntityPath(item) {
  switch (item.entity_type) {
    case 'MSA':
      return `/msa/${item.entity_id}`;
    case 'SOW':
      return `/sow/${item.entity_id}`;
    case 'Timesheet':
      return `/timesheets/${item.entity_id}`;
    case 'Engagement':
      return `/engagements/${item.entity_id}`;
    default:
      return `/invoices/${item.entity_id}`;
  }
}

export default function HeaderUserActions() {
  const { user, canView } = useAuth();
  const notifWrapRef = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pendingRaw, setPendingRaw] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [dismissed, setDismissed] = useState(() => new Set());
  const [readIds, setReadIds] = useState(() => new Set());
  const [notifPrefsHydrated, setNotifPrefsHydrated] = useState(false);

  const refreshPending = useCallback(() => {
    if (!user?.id) return Promise.resolve();
    return api
      .get('/approvals/pending')
      .then((p) => {
        const list = p || [];
        const keys = new Set(list.map(notifKey));
        setDismissed((prev) => new Set([...prev].filter((k) => keys.has(k))));
        setReadIds((prev) => new Set([...prev].filter((k) => keys.has(k))));
        setPendingRaw(list);
      })
      .catch(() => setPendingRaw([]));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setNotifPrefsHydrated(false);
      setDismissed(new Set());
      setReadIds(new Set());
      setPendingRaw([]);
      return;
    }
    const { dismissed: d, read: r } = loadNotifPrefs(user.id);
    setDismissed(d);
    setReadIds(r);
    setNotifPrefsHydrated(true);
    refreshPending();
  }, [user?.id, refreshPending]);

  useEffect(() => {
    if (!user?.id || !notifPrefsHydrated) return;
    saveNotifPrefs(user.id, dismissed, readIds);
  }, [user?.id, notifPrefsHydrated, dismissed, readIds]);

  const { visibleItems, unreadCount } = useMemo(() => {
    const visible = pendingRaw.filter((i) => !dismissed.has(notifKey(i)));
    const unread = visible.filter((i) => !readIds.has(notifKey(i))).length;
    return { visibleItems: visible, unreadCount: unread };
  }, [pendingRaw, dismissed, readIds]);

  useEffect(() => {
    if (!notifOpen) return;
    setNotifLoading(true);
    refreshPending().finally(() => setNotifLoading(false));
  }, [notifOpen, refreshPending]);

  useEffect(() => {
    if (!notifOpen) return;
    const onDoc = (e) => {
      if (notifWrapRef.current && !notifWrapRef.current.contains(e.target)) setNotifOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setNotifOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [notifOpen]);

  const markOneRead = (item) => {
    const k = notifKey(item);
    setReadIds((prev) => new Set(prev).add(k));
  };

  const dismissOne = (item) => {
    const k = notifKey(item);
    setDismissed((prev) => new Set(prev).add(k));
    setReadIds((prev) => {
      const next = new Set(prev);
      next.delete(k);
      return next;
    });
  };

  const markAllRead = () => {
    setReadIds((prev) => {
      const next = new Set(prev);
      pendingRaw.forEach((i) => {
        if (!dismissed.has(notifKey(i))) next.add(notifKey(i));
      });
      return next;
    });
  };

  const clearAllFromPanel = () => {
    setDismissed((prev) => {
      const next = new Set(prev);
      pendingRaw.forEach((i) => next.add(notifKey(i)));
      return next;
    });
  };

  return (
    <div className="header-user-actions" aria-label="Account shortcuts">
      <div className="header-user-notif-wrap" ref={notifWrapRef}>
        <button
          type="button"
          className={`header-user-icon-btn${notifOpen ? ' header-user-icon-btn--active' : ''}`}
          aria-label="Notifications"
          aria-expanded={notifOpen}
          aria-haspopup="dialog"
          title="Workflow notifications"
          onClick={() => setNotifOpen((o) => !o)}
        >
          <span className="header-user-bell-wrap">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {unreadCount > 0 ? (
              <span className="header-user-notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            ) : null}
          </span>
        </button>
        {notifOpen ? (
          <div className="header-user-notif-panel" role="dialog" aria-label="Workflow notifications">
            <div className="header-user-notif-panel-head">
              <div className="header-user-notif-panel-head-row">
                <strong>Notifications</strong>
                <span className="header-user-notif-sub">
                  {notifLoading
                    ? 'Loading…'
                    : pendingRaw.length === 0
                      ? 'Nothing in queue'
                      : `${visibleItems.length} shown · ${unreadCount} unread`}
                </span>
              </div>
              {!notifLoading && pendingRaw.length > 0 ? (
                <div className="header-user-notif-panel-head-actions">
                  <button
                    type="button"
                    className="header-user-notif-action-btn"
                    disabled={unreadCount === 0}
                    onClick={markAllRead}
                  >
                    Mark all read
                  </button>
                  <button
                    type="button"
                    className="header-user-notif-action-btn header-user-notif-action-btn--danger"
                    disabled={visibleItems.length === 0}
                    onClick={clearAllFromPanel}
                  >
                    Clear all
                  </button>
                </div>
              ) : null}
            </div>
            <div className="header-user-notif-panel-body">
              {notifLoading ? <p className="header-user-notif-empty">Loading…</p> : null}
              {!notifLoading && pendingRaw.length === 0 ? (
                <p className="header-user-notif-empty">No pending workflow items for your account.</p>
              ) : null}
              {!notifLoading && pendingRaw.length > 0 && visibleItems.length === 0 ? (
                <p className="header-user-notif-empty">
                  All items are hidden from this panel. They still appear in the full approvals queue until they are
                  approved or rejected.
                </p>
              ) : null}
              {!notifLoading &&
                visibleItems.slice(0, 8).map((item) => {
                  const k = notifKey(item);
                  const isRead = readIds.has(k);
                  return (
                    <div
                      key={k}
                      className={`header-user-notif-row${isRead ? ' header-user-notif-row--read' : ''}`}
                    >
                      <Link
                        to={approvalEntityPath(item)}
                        className="header-user-notif-row-main"
                        onClick={() => setNotifOpen(false)}
                      >
                        <span className="header-user-notif-type">{item.entity_type}</span>
                        <span className="header-user-notif-body-text">
                          <span className="header-user-notif-ref">{item.ref || item.entity_id}</span>
                          {item.client_name ? (
                            <span className="header-user-notif-client">{item.client_name}</span>
                          ) : null}
                        </span>
                      </Link>
                      <div className="header-user-notif-row-actions">
                        <button
                          type="button"
                          className="header-user-notif-icon-btn"
                          title="Mark as read"
                          aria-label={`Mark as read: ${item.entity_type} ${item.ref || item.entity_id}`}
                          disabled={isRead}
                          onClick={(e) => {
                            e.preventDefault();
                            markOneRead(item);
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                              d="M20 6L9 17l-5-5"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="header-user-notif-icon-btn header-user-notif-icon-btn--remove"
                          title="Remove from list"
                          aria-label={`Remove from list: ${item.entity_type} ${item.ref || item.entity_id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            dismissOne(item);
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                            <path
                              d="M6 6l12 12M18 6L6 18"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="header-user-notif-panel-foot">
              {canView('approvals') ? (
                <Link to="/approvals" className="header-user-notif-foot-link" onClick={() => setNotifOpen(false)}>
                  Open full approvals queue
                </Link>
              ) : (
                <span className="header-user-notif-foot-hint">
                  Approvals may be assigned to you even when the menu entry is hidden.
                </span>
              )}
            </div>
          </div>
        ) : null}
      </div>
      <Link
        to="/profile"
        className="header-user-icon-btn"
        aria-label="My profile"
        title={`${user?.full_name || 'User'} — profile & account`}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
        </svg>
      </Link>
    </div>
  );
}
